import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'motion/react';
import { getTradeLock } from './helpers';
import { API, apiFetch } from '../api';
import { EXT_EVENT, EXT_REQUEST, EXT_READY_EVENT, requestExtensionStatus } from '../extensionEvents';
import {
  ZONE, fmt, rise,
  ZoneHead, ZoneStats, ZoneSearch, ZoneChips, ZoneSelect, ZoneEmpty, ZoneFooter,
} from './zoneShared';
import { TradePartnerBadge } from './TradePartner';
import ItemDetailModal, { DetailButton } from './ItemDetailModal';
import { CharmSlot } from './StickerSlots';

// Inventory Zone — "THE LOCKER": a full port of the Inventory tab onto the
// Overview 7 board. Everything the original card carries is here — badges,
// the five sticker slots with click-to-copy, the wear bar, the float/pattern
// readout, trade-lock chip, price block and inspect link — plus the extension
// panel, sync metadata, group selection and saved groups.
const EXTERIOR_ABBR = {
  'Factory New': 'FN', 'Minimal Wear': 'MW', 'Field-Tested': 'FT',
  'Well-Worn': 'WW', 'Battle-Scarred': 'BS',
};
const EXTERIOR_RE = /\s*\((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/;

const MAX_SLOTS = 5;

const FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'locked',    label: 'Trade locked' },
  { key: 'tradable',  label: 'Tradable' },
  { key: 'tracked',   label: 'In tracker' },
  { key: 'untracked', label: 'Not tracked' },
];

// Storage is not one of the filters above, it is a different list. Everything
// above narrows what Steam can currently see; items in a storage unit are not
// in that reading at all, so they cannot be a subset of it. Appended only when
// there is something to look at. See the twin in SkinTable.jsx.
const STORAGE_FILTER = { key: 'stored', label: 'Storage' };

const SORTS = [
  { key: 'price',    label: 'Highest price' },
  { key: 'name',     label: 'Name A-Z' },
  { key: 'float',    label: 'Lowest float' },
  { key: 'stickers', label: 'Most stickers' },
  { key: 'unlock',   label: 'Unlocking soonest' },
];

const fmtTime = iso => {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtPrice = v => {
  if (v == null) return 'n/a';
  return '$' + fmt(v);
};

/* ═══════════════════════════════════════════════════════════
   Extension panel — same bridge/token/sync logic as the
   original ExtensionPanel, re-inked for the board.
   ═══════════════════════════════════════════════════════════ */
function ZoneExtensionPanel({ user }) {
  const [extensionPresent, setExtensionPresent] = useState(false);
  const [extStatus, setExtStatus]               = useState(null);
  const [linkingToken, setLinkingToken]         = useState(null);
  const [tokenExpiry, setTokenExpiry]           = useState(null);
  const [password, setPassword]                 = useState('');
  const [showPasswordBox, setShowPasswordBox]   = useState(false);
  const [syncing, setSyncing]                   = useState(false);
  const [syncProgress, setSyncProgress]         = useState('');
  const [error, setError]                       = useState('');
  const [success, setSuccess]                   = useState('');

  // ── Detect extension via custom event ──────────────────────
  useEffect(() => {
    const handler = () => setExtensionPresent(true);
    document.addEventListener(EXT_READY_EVENT, handler);
    // Probe: ask for status in case the ready event already fired before this mounted
    requestExtensionStatus();
    // Second probe after a short delay to handle slower content-script injection
    const t = setTimeout(requestExtensionStatus, 800);
    return () => { document.removeEventListener(EXT_READY_EVENT, handler); clearTimeout(t); };
  }, []);

  // ── Listen for messages from content script ────────────────
  useEffect(() => {
    function onMessage(event) {
      if (event.source !== window) return;
      const { type } = event.data || {};

      if (type === EXT_EVENT.STATUS) {
        setExtStatus(event.data);
        setExtensionPresent(true); // present regardless of whether linked
      }
      if (type === EXT_EVENT.LINK_RESULT) {
        if (event.data.success) {
          setLinkingToken(null);
          setShowPasswordBox(false);
          setPassword('');
          setSuccess('Extension connected successfully!');
          requestExtensionStatus();
        } else {
          setError(event.data.error || 'Linking failed');
        }
      }
      if (type === EXT_EVENT.SYNC_RESULT) {
        setSyncing(false);
        if (event.data.success) setSuccess(`Synced ${event.data.itemCount} items.`);
        else setError(event.data.error || 'Sync failed');
        requestExtensionStatus();
      }
      if (type === EXT_EVENT.SYNC_PROGRESS) setSyncProgress(event.data.message || '');
      if (type === EXT_EVENT.SYNC_COMPLETE) {
        setSyncing(false);
        setSyncProgress('');
        // A background sync (alarm or popup) also lands here, and the panel's
        // item count / last-sync are stale until we ask again.
        requestExtensionStatus();
      }
      if (type === EXT_EVENT.SYNC_ERROR) { setSyncing(false); setSyncProgress(''); setError(event.data.error || 'Sync failed'); }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // ── Generate linking token ─────────────────────────────────
  // Session first, password as the fallback. Same reasoning as the classic
  // panel in ExtensionPanel.jsx: a Steam-created account has no password, so
  // asking for one first locks it out of the extension permanently.
  const generateToken = useCallback(async () => {
    setError('');
    try {
      const res  = password
        ? await fetch(`${API}/api/ext-auth/linking-token`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ username: user, password }),
          })
        : await apiFetch('/api/ext-auth/linking-token', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    '{}',
          });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate token');

      setLinkingToken(data.linkingToken);
      setTokenExpiry(data.expiresAt);
      setPassword('');
      setShowPasswordBox(false);

      // Send token to extension via content script bridge
      window.postMessage({ type: EXT_REQUEST.LINK_TOKEN, token: data.linkingToken }, window.location.origin);
      setSuccess('Token generated and sent to extension. Check the extension popup.');
    } catch (err) {
      setError(err.message);
      if (!password) setShowPasswordBox(true);
    }
  }, [password, user]);

  const triggerSync = () => {
    setError('');
    setSyncing(true);
    setSyncProgress('Requesting sync…');
    window.postMessage({ type: EXT_REQUEST.SYNC }, window.location.origin);
  };

  const isConnected = extStatus?.connected;
  const statusText  = isConnected ? 'Connected' : extensionPresent ? 'Extension detected' : 'Not detected';
  const statusBg    = isConnected ? ZONE.lime : extensionPresent ? ZONE.yellow : ZONE.pink;

  return (
    <div className="zn-panel" style={{ marginBottom: 26 }}>
      <div className="zn-panel-head">
        <div className="zn-panel-h">Browser extension</div>
        <div className="zn-panel-tools">
          <span className="zn-sticker" style={{ background: statusBg, boxShadow: '3px 3px 0 #131313' }}>{statusText}</span>
        </div>
      </div>

      <div className="zn-panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {isConnected && (
          <div className="zn-ext-status">
            <span className="k">Account</span><span className="v">{extStatus.username}</span>
            <span className="k">Steam ID</span><span className="v mono">{extStatus.steamId || 'n/a'}</span>
            <span className="k">Items</span><span className="v">{extStatus.itemCount != null ? `${extStatus.itemCount} items` : 'n/a'}</span>
            <span className="k">Last sync</span><span className="v">{fmtTime(extStatus.lastSync)}</span>
          </div>
        )}

        {syncing && (
          <div>
            <div className="zn-meter">
              <motion.div className="fill" style={{ background: ZONE.purple }}
                initial={{ width: '10%' }} animate={{ width: ['10%', '90%', '10%'] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }} />
            </div>
            <div style={{ fontSize: 14.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 7 }}>
              {syncProgress}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {isConnected ? (
            <button type="button" className="zn-btn primary" onClick={triggerSync} disabled={syncing}>
              {syncing ? 'Syncing…' : 'Sync inventory'}
            </button>
          ) : extensionPresent ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
              {!showPasswordBox ? (
                <button type="button" className="zn-btn primary" style={{ alignSelf: 'flex-start' }}
                  onClick={generateToken}>
                  Connect extension
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input type="password" className="zn-input" value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && generateToken()}
                    placeholder="Enter your password to link…" />
                  <button type="button" className="zn-btn primary" onClick={generateToken}>Generate token</button>
                  <button type="button" className="zn-btn" onClick={() => { setShowPasswordBox(false); setPassword(''); }}>
                    Cancel
                  </button>
                </div>
              )}
              {linkingToken && (
                <div style={{ fontSize: 14.5, fontWeight: 700, color: ZONE.mute }}>
                  Token sent to extension. Expires: {tokenExpiry ? new Date(tokenExpiry).toLocaleTimeString() : 'n/a'}
                </div>
              )}
            </div>
          ) : (
            <p style={{ fontSize: 15, fontWeight: 700, color: ZONE.mute, margin: 0, lineHeight: 1.5 }}>
              Install the CS2 Profit Tracker browser extension to enable automatic inventory sync.
              See <code>extension/README.md</code> for installation instructions.
            </p>
          )}
        </div>

        {error   && <p className="zn-ext-msg bad">{error}</p>}
        {success && <p className="zn-ext-msg good">{success}</p>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Item card
   ═══════════════════════════════════════════════════════════ */
function ZoneInventoryCard({ item, price, selected, tracked, stored, onToggle, onPeek, stickerPrices, index, tradePartner }) {
  const [copiedSlot, setCopiedSlot]   = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);

  const mhn           = item.marketHashName || item.market_hash_name || '';
  const exteriorMatch = mhn.match(EXTERIOR_RE);
  const exterior      = exteriorMatch ? exteriorMatch[1] : null;
  const baseName      = mhn.replace(EXTERIOR_RE, '').trim();
  const isST          = baseName.startsWith('StatTrak™');
  const isSV          = baseName.startsWith('Souvenir');
  const displayName   = baseName.replace('StatTrak™ ', '').replace('Souvenir ', '');
  const abbr          = EXTERIOR_ABBR[exterior] || null;

  const stickers = item.stickers || [];
  const slots    = Array.from({ length: MAX_SLOTS }, (_, i) => stickers[i] || null);
  const hasSkin  = exterior != null;

  const float = item.float;
  const wear  = float != null ? float * 100 : null;

  // Chips: trade lock only — float/pattern get their own dedicated labeled row.
  // A stored item's last trade-lock reading predates it being put away, so it
  // is dropped rather than shown; where it sits is the only fact left.
  const { locked: rawLocked, until } = getTradeLock(item, null);
  const locked = stored ? false : rawLocked;
  const chips = stored
    ? ['In storage']
    : locked
      ? [until
          ? `Locked ${until.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
          : 'Locked']
      : [];

  return (
    <motion.article
      className={`zn-inv-card${selected ? ' selected' : ''}${tracked || stored ? ' tracked' : ''}${locked ? ' on-hold' : ''}`}
      onClick={() => !tracked && !stored && onToggle(item.assetId)}
      {...rise(index)}
    >
      {/* 1. Name row */}
      <div className="zn-inv-name-row">
        <span className="zn-inv-name" title={mhn}>{displayName || mhn}</span>
        <div className="zn-inv-badges">
          {stored && <span className="zn-inv-badge tracked">stored</span>}
          {tracked && <span className="zn-inv-badge tracked">tracked</span>}
          {abbr && <span className="zn-inv-badge ext">{abbr}</span>}
          {isST && <span className="zn-inv-badge st">ST™</span>}
          {isSV && <span className="zn-inv-badge sv">SV</span>}
        </div>
      </div>
      {/* 1b. Where it came from — the slot is always in the layout, filled or
          not, so a card that arrived in a trade lines up with one that did
          not. Rendering it conditionally pushed the picture and everything
          under it down on trade cards only. */}
      <div className="zn-inv-from">
        {tradePartner && <TradePartnerBadge partner={tradePartner} flat style={{ minWidth: 0 }} />}
      </div>

      {/* 2. Image */}
      <div className="zn-inv-img">
        {/* The same flag a Live card flies, class and wording included. */}
        {locked && (
          <span className="zn-item-flag">
            {until
              ? `LOCK ${until.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
              : 'LOCKED'}
          </span>
        )}
        {item.iconUrl
          ? <img src={item.iconUrl} alt={displayName} loading="lazy" />
          : <div className="zn-thumb-blank" style={{ width: '70%', height: 62 }} />}
      </div>

      {/* 3. Sticker row — always rendered; hidden for non-skin items */}
      <div className="zn-inv-stickers"
        style={{ visibility: hasSkin ? 'visible' : 'hidden', pointerEvents: hasSkin ? 'auto' : 'none' }}>
        {slots.map((sticker, i) => {
          const sp     = sticker?.name ? stickerPrices?.['Sticker | ' + sticker.name]?.starting_at?.price : null;
          const wPct   = sticker?.wear != null ? Math.round(sticker.wear * 100) : null;
          const copied = copiedSlot === i;
          return (
            <div key={i}
              className={`zn-inv-slot${sticker ? ' filled' : ''}${copied ? ' copied' : ''}`}
              onMouseEnter={() => sticker && setHoveredSlot(i)}
              onMouseLeave={() => setHoveredSlot(null)}
              onClick={sticker ? e => {
                e.stopPropagation();
                navigator.clipboard?.writeText(sticker.name || '').then(() => {
                  setCopiedSlot(i);
                  setTimeout(() => setCopiedSlot(null), 1400);
                }).catch(() => {});
              } : undefined}>
              <span className="zn-inv-slot-num">{i + 1}</span>
              {sticker?.image && <img src={sticker.image} alt="" />}
              {sticker && (hoveredSlot === i || copied) && (
                <div className={`zn-inv-tip${copied ? ' copied' : ''}`}>
                  {copied ? (
                    <span className="copied-text">Copied!</span>
                  ) : (
                    <>
                      <span className="name">{sticker.name}</span>
                      {sp != null && <span className="price">${fmt(sp)}</span>}
                      {wPct != null && wPct > 0 && <span className="hint">{wPct}% worn</span>}
                      <span className="hint">Click to copy</span>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {/* The charm rides on the end of the same rail. Slots are flex:1, so
            an item wearing one gives up a little width across the row rather
            than needing a line of its own. */}
        {(item.charms || []).map((charm, i) => (
          <CharmSlot key={`charm-${i}`} charm={charm} prices={stickerPrices} variant="zone" />
        ))}
      </div>

      {/* 4. Wear bar — always rendered; marker appears only when float exists */}
      <div className="zn-inv-wear">
        <div className={`zn-inv-wear-track${wear == null ? ' empty' : ''}`}>
          {wear != null && (
            <div className="zn-inv-wear-marker" style={{ left: `${Math.min(Math.max(wear, 0), 100)}%` }} />
          )}
        </div>
      </div>

      {/* 5. Float + pattern readout — always visible */}
      <div className="zn-inv-fp">
        <div className="zn-inv-fp-cell">
          <span className="zn-inv-fp-label">Float</span>
          <span className={`zn-inv-fp-value${float == null ? ' empty' : ''}`}>
            {float != null ? float.toFixed(6) : 'n/a'}
          </span>
        </div>
        <div className="zn-inv-fp-cell">
          <span className="zn-inv-fp-label">Pattern</span>
          <span className={`zn-inv-fp-value${item.paintSeed == null ? ' empty' : ''}`}>
            {item.paintSeed != null ? `#${item.paintSeed}` : 'n/a'}
          </span>
        </div>
      </div>

      {/* 6. Meta chips — trade lock only. The row always occupies its slot so
          locked and unlocked cards stay on the same template. */}
      <div className="zn-inv-chips">
        {chips.map((c, i) => <span key={i} className="zn-inv-chip">{c}</span>)}
      </div>

      {/* 7. Spacer — pushes price + button to the bottom */}
      <span className="zn-inv-spacer" />

      {/* 8. Price section */}
      <div className="zn-inv-prices">
        <div className="zn-inv-price-row">
          <span className="k">Market</span>
          <span className="v">{price != null ? `$${fmt(price)}` : 'n/a'}</span>
        </div>
        <div className="zn-inv-price-row">
          <span className="k">Status</span>
          <span className="v small">
            {stored ? 'In storage' : tracked ? 'In tracker' : selected ? 'In group' : 'Not tracked'}
          </span>
        </div>
      </div>

      {/* 9. Inspect button + details peek */}
      <div className="zn-inv-actions">
        {item.inspectLink
          ? <a href={item.inspectLink} className="zn-inv-inspect"
              onClick={e => e.stopPropagation()} target="_blank" rel="noreferrer">
              Inspect in game
            </a>
          : <span className="zn-inv-inspect off">No inspect link</span>}
        <DetailButton corner={false} onOpen={() => onPeek(item)} />
      </div>
    </motion.article>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab
   ═══════════════════════════════════════════════════════════ */
export default function InventoryTabZone({
  user, livePrices, csfloatPrices, syncedInventory, setSyncedInventory, storedItems = [],
  selectedForGroup, setSelectedForGroup,
  groupedPurchases, setGroupedPurchases,
  onOpenGroupModal, trackedAssetIds, tradePartners, onSendToTracker,
  lendingEnabled = false, onLendItems,
}) {
  const [loading, setLoading]         = useState(false);
  const [syncMeta, setSyncMeta]       = useState(null);
  const [localGroups, setLocalGroups] = useState(groupedPurchases || []);
  const [query, setQuery]             = useState('');
  const [filter, setFilter]           = useState('all');
  const [sort, setSort]               = useState('price');
  const [detail, setDetail]           = useState(null);   // the asset whose modal is open

  // Load inventory meta + grouped purchases on mount
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // Reload inventory (picks up fresh float/pattern data after a sync)
        const res  = await apiFetch(`/api/tracker/inventory/${user}`);
        const data = await res.json();
        if (data.items?.length) {
          setSyncedInventory(data.items);
          setSyncMeta(data.syncMeta || null);
        } else {
          setSyncMeta(data.syncMeta || null);
        }
        const gRes  = await apiFetch(`/api/grouped-purchases/${user}`);
        const gData = await gRes.json();
        if (gData.groupedPurchases) {
          setGroupedPurchases(gData.groupedPurchases);
          setLocalGroups(gData.groupedPurchases);
        }
      } catch { /* non-fatal */ }
      setLoading(false);
    }
    load();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setLocalGroups(groupedPurchases || []); }, [groupedPurchases]);

  // Stored items are read-only here: Steam reissues the assetId when one comes
  // back out of the unit, so grouping or tracking it would file a dead id.
  const storedAssetIds = useMemo(
    () => new Set((storedItems || []).map(i => i.assetId).filter(Boolean)),
    [storedItems]
  );

  const toggleItem = assetId => {
    if (trackedAssetIds?.has(assetId)) return;
    if (storedAssetIds.has(assetId)) return;
    setSelectedForGroup(prev => {
      const next = new Set(prev);
      next.has(assetId) ? next.delete(assetId) : next.add(assetId);
      return next;
    });
  };

  const items = useMemo(() => (syncedInventory || []).map(it => {
    const mhn = it.marketHashName || it.market_hash_name || '';
    const { locked, until } = getTradeLock(it, null);
    return {
      it, mhn,
      price:    livePrices?.[mhn]?.starting_at?.price ?? null,
      locked, until,
      stickers: (it.stickers || []).filter(s => s.name).length,
      tracked:  trackedAssetIds?.has(it.assetId) ?? false,
    };
  }), [syncedInventory, livePrices, trackedAssetIds]);

  // The storage shelf, same shape so one card and one sort serve both. Trade
  // lock is blanked rather than read: the last reading of it predates the item
  // being put away and says nothing about now.
  const stored = useMemo(() => (storedItems || []).map(it => {
    const mhn = it.marketHashName || it.market_hash_name || '';
    return {
      it, mhn,
      price:    livePrices?.[mhn]?.starting_at?.price ?? null,
      locked: false, until: null,
      stickers: (it.stickers || []).filter(s => s.name).length,
      tracked:  trackedAssetIds?.has(it.assetId) ?? false,
      stored:   true,
    };
  }), [storedItems, livePrices, trackedAssetIds]);

  const chips = useMemo(
    () => (stored.length ? [...FILTERS, STORAGE_FILTER] : FILTERS),
    [stored.length]
  );

  // The last stored item coming back takes its chip with it. Without this the
  // view would sit on a filter that no longer has a button to leave it by.
  useEffect(() => {
    if (filter === 'stored' && stored.length === 0) setFilter('all');
  }, [filter, stored.length]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Two separate pools, never mixed: nothing in storage is visible in Steam,
    // so no filter over the live inventory can honestly include it.
    const pool = filter === 'stored' ? stored : items;
    const list = pool.filter(e => {
      if (q && !e.mhn.toLowerCase().includes(q)) return false;
      if (filter === 'locked'    && !e.locked) return false;
      if (filter === 'tradable'  && e.locked) return false;
      if (filter === 'tracked'   && !e.tracked) return false;
      if (filter === 'untracked' && e.tracked) return false;
      return true;
    });
    const by = {
      price:    (a, b) => (b.price ?? -1) - (a.price ?? -1),
      name:     (a, b) => a.mhn.localeCompare(b.mhn),
      float:    (a, b) => (a.it.float ?? Infinity) - (b.it.float ?? Infinity),
      stickers: (a, b) => b.stickers - a.stickers,
      unlock:   (a, b) => (a.until ? +a.until : Infinity) - (b.until ? +b.until : Infinity),
    }[sort];
    return list.sort(by);
  }, [items, stored, query, filter, sort]);

  // Live items only. The headline numbers answer "what is in my Steam
  // inventory", and something sitting in a casket is not part of that answer.
  const totals = useMemo(() => {
    const value       = items.reduce((s, e) => s + (e.price ?? 0), 0);
    const locked      = items.filter(e => e.locked).length;
    const lockedValue = items.filter(e => e.locked).reduce((s, e) => s + (e.price ?? 0), 0);
    const tracked     = items.filter(e => e.tracked).length;
    const priced      = items.filter(e => e.price != null).length;
    return { value, locked, lockedValue, tracked, priced, count: items.length };
  }, [items]);

  const shownValue = useMemo(() => filtered.reduce((s, e) => s + (e.price ?? 0), 0), [filtered]);
  const selCount   = selectedForGroup?.size ?? 0;

  return (
    <div className="zn-root">

      <ZoneHead
        pre="THE" hl="LOCKER"
        sub="Your synced Steam inventory. Click items to build a group, or send them straight to the tracker."
        stickers={[
          { text: `${totals.count} items synced`, bg: ZONE.teal },
          totals.locked > 0 && { text: `${totals.locked} trade locked`, bg: ZONE.orange },
        ]}
      />

      {/* ── Extension panel ────────────────────────── */}
      <ZoneExtensionPanel user={user} />

      {/* ── Sync metadata ──────────────────────────── */}
      {syncMeta && (
        <div className="zn-footer" style={{ marginTop: 0, marginBottom: 26, paddingTop: 0, borderTop: 'none' }}>
          <span className="zn-sticker" style={{ background: ZONE.white, transform: 'rotate(-1deg)' }}>
            last sync: {fmtTime(syncMeta.syncCompletedAt)}
          </span>
          <span className="zn-sticker" style={{ background: ZONE.purple, transform: 'rotate(1deg)' }}>
            total: {syncMeta.totalItemCount ?? 0}
          </span>
          <span className="zn-sticker" style={{ background: ZONE.lime, transform: 'rotate(-1.4deg)' }}>
            tradable: {syncMeta.tradableItemCount ?? 0}
          </span>
          <span className="zn-sticker" style={{ background: ZONE.orange, transform: 'rotate(1.2deg)' }}>
            trade-locked: {syncMeta.protectedItemCount ?? 0}
          </span>
        </div>
      )}

      {/* ── Stats ──────────────────────────────────── */}
      {totals.count > 0 && (
        <ZoneStats items={[
          { label: 'Locker value', value: `$${fmt(totals.value)}`,
            sub: `${totals.priced} of ${totals.count} have a live price`, bg: ZONE.yellow },
          { label: 'Locked up', value: `$${fmt(totals.lockedValue)}`,
            sub: `${totals.locked} item${totals.locked !== 1 ? 's' : ''} you can't trade yet`, bg: ZONE.orange },
          { label: 'In the tracker', value: String(totals.tracked),
            sub: `${totals.count - totals.tracked} still untracked`, bg: ZONE.lime },
        ]} />
      )}

      {/* ── Selection actions ──────────────────────── */}
      {selCount > 0 && (
        <motion.div className="zn-sel-bar"
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <span className="zn-sel-count">{selCount} selected</span>
          <button type="button" className="zn-btn go"
            onClick={() => onSendToTracker && onSendToTracker(Array.from(selectedForGroup), false)}>
            → Live ({selCount})
          </button>
          <button type="button" className="zn-btn invest"
            onClick={() => onSendToTracker && onSendToTracker(Array.from(selectedForGroup), true)}>
            → Investments ({selCount})
          </button>
          <button type="button" className="zn-btn" onClick={onOpenGroupModal}>Group</button>
          {/* Lending out of the locker: the items are picked already, so this
              jumps straight to "who has it". Only tracked items can be lent,
              since a lend is an overlay on a tracker row. */}
          {lendingEnabled && (
            <button type="button" className="zn-btn"
              onClick={() => onLendItems && onLendItems(Array.from(selectedForGroup))}>
              Lend out ({selCount})
            </button>
          )}
          <button type="button" className="zn-btn" style={{ marginLeft: 'auto' }}
            onClick={() => setSelectedForGroup(new Set())}>
            Clear
          </button>
        </motion.div>
      )}

      {/* ── Toolbar ────────────────────────────────── */}
      {(totals.count > 0 || stored.length > 0) && (
        <div className="zn-bar">
          <ZoneSearch value={query} onChange={setQuery} placeholder="Search the locker…" />
          <ZoneChips options={chips} value={filter} onChange={setFilter} />
          <span className="zn-count" style={{ marginLeft: 'auto' }}>
            {filtered.length} shown · ${fmt(shownValue)}
          </span>
          <ZoneSelect value={sort} onChange={setSort} options={SORTS} label="Sort locker" />
        </div>
      )}

      {/* ── Grid ───────────────────────────────────── */}
      {loading && totals.count === 0 ? (
        <div className="zn-inv-grid">
          {Array.from({ length: 10 }).map((_, i) => <div key={i} className="zn-inv-skeleton" />)}
        </div>
      ) : totals.count === 0 && stored.length === 0 ? (
        <ZoneEmpty>
          The locker is empty. Connect the browser extension above and hit sync to pull your Steam inventory in.
        </ZoneEmpty>
      ) : filtered.length === 0 ? (
        <ZoneEmpty>Nothing matches your filters.</ZoneEmpty>
      ) : (
        <>
          {filter === 'stored' && (
            <div className="zn-empty" style={{ textAlign: 'left', marginBottom: 16 }}>
              Items you said are in a storage unit. Steam does not list what is inside one, so these
              are here on your word alone and are left out of every other view and total. They come
              back on their own the next time a sync sees them.
            </div>
          )}
          <div className="zn-inv-grid">
            {filtered.map((e, i) => (
              <ZoneInventoryCard
                key={e.it.assetId || i}
                index={i}
                item={e.it}
                price={e.price}
                selected={selectedForGroup?.has(e.it.assetId) ?? false}
                tracked={e.tracked}
                stored={e.stored}
                onToggle={toggleItem}
                onPeek={setDetail}
                stickerPrices={livePrices}
                tradePartner={tradePartners?.byAssetId?.get(e.it.assetId)}
              />
            ))}
          </div>
        </>
      )}

      {/* ── Saved groups ───────────────────────────── */}
      {localGroups.length > 0 && (
        <div style={{ marginTop: 30 }}>
          <div className="zn-panel-title free">Saved groups ({localGroups.length})</div>
          {localGroups.map(gp => {
            const p = gp.displayPercentage;
            const displayPct = (p % 1 === 0 ? p.toFixed(0) : p.toFixed(1)) + '%';
            return (
              <div key={gp.id} className="zn-group">
                <div className="zn-group-top">
                  <span className="zn-group-title">
                    {gp.items.length} item{gp.items.length !== 1 ? 's' : ''} · paid {fmtPrice(gp.totalPurchasePrice)}
                  </span>
                  <span className="zn-group-pct">{displayPct} of market</span>
                </div>
                <div className="zn-group-meta">
                  <span>Market value at purchase: {fmtPrice(gp.combinedMarketValueAtCreation)}</span>
                  <span>Created: {fmtTime(gp.createdAt)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ZoneFooter stickers={[
        { text: 'synced via the browser extension', bg: ZONE.purple },
        { text: 'click a sticker to copy its name', bg: ZONE.white },
        { text: `locker worth $${fmt(totals.value)}`, bg: ZONE.orange, right: true },
      ]} />

      {detail && (() => {
        const { locked, until } = getTradeLock(detail, null);
        return (
          <ItemDetailModal
            variant="zone"
            kind="inventory"
            name={detail.marketHashName || detail.market_hash_name || ''}
            image={detail.iconUrl}
            livePrices={livePrices}
            csfloatPrices={csfloatPrices}
            float={detail.float}
            paintSeed={detail.paintSeed}
            tradeLock={locked
              ? (until ? `locked until ${until.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'trade locked')
              : null}
            onClose={() => setDetail(null)}
          />
        );
      })()}
    </div>
  );
}
